import {
  type ApprovalStatus,
  api,
  type CapabilityView,
  isPlatformError,
  type ModelCatalogQuery,
} from '@agentic/contracts';
import { infiniteQueryOptions, QueryClient, queryOptions } from '@tanstack/react-query';
import { client } from './api.ts';
import { notify } from './notify.ts';

/** Query keys in one place so SSE invalidation stays consistent. */
export const keys = {
  me: ['me'] as const,
  task: (id: string) => ['task', id] as const,
  trace: (id: string) => ['task', id, 'trace'] as const,
  /** 23: `GET /tasks/{taskId}/model-calls` (under the task prefix, so task invalidation refreshes it). */
  modelCalls: (id: string) => ['task', id, 'model-calls'] as const,
  ledger: (id: string) => ['task', id, 'ledger'] as const,
  audit: (taskId?: string) => ['audit', taskId ?? 'all'] as const,
  approvals: (status?: ApprovalStatus | 'all', taskId?: string) =>
    ['approvals', status ?? 'all', taskId ?? ''] as const,
  approvalsAll: ['approvals'] as const,
  approval: (id: string) => ['approval', id] as const,
  agents: ['registry', 'agents'] as const,
  tools: ['registry', 'tools'] as const,
  models: ['registry', 'models'] as const,
  capabilities: ['registry', 'capabilities'] as const,
  modules: ['modules'] as const,
  sources: ['knowledge', 'sources'] as const,
  health: ['admin', 'health'] as const,
  stats: ['admin', 'stats'] as const,
  scenarios: ['demo', 'scenarios'] as const,
  /** 23: model management (`/registry/model-management/*`); invalidate the prefix after any management mutation. */
  modelManagement: ['model-management'] as const,
  mmStatus: ['model-management', 'status'] as const,
  mmConnections: ['model-management', 'connections'] as const,
  mmCatalog: (query: Omit<ModelCatalogQuery, 'limit' | 'cursor'>) =>
    ['model-management', 'catalog', query] as const,
  mmProfiles: ['model-management', 'profiles'] as const,
  mmProfile: (name: string) => ['model-management', 'profile', name] as const,
  mmAudit: (target?: string) => ['model-management', 'audit', target ?? 'all'] as const,
};

let unauthorizedHandler: (() => void) | undefined;

/** Runs the app's expired-session handler (the one given to `createQueryClient`), e.g. from a closed event stream. */
export function reportUnauthorized(): void {
  unauthorizedHandler?.();
}

/** Query client with toasts for background failures and a hook for expired sessions. */
export function createQueryClient(options: { onUnauthorized?: () => void } = {}): QueryClient {
  if (options.onUnauthorized) unauthorizedHandler = options.onUnauthorized;
  const qc = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5_000,
        retry: (count, error) => !(isPlatformError(error) && !error.retryable) && count < 2,
        refetchOnWindowFocus: true,
      },
      mutations: {
        onError: (error, _vars, _ctx, mutation) => {
          if (isPlatformError(error) && error.code === 'unauthorized') options.onUnauthorized?.();
          if (mutation.meta?.silent) return;
          notify.error(error);
        },
      },
    },
  });
  qc.getQueryCache().config.onError = (error, query) => {
    if (isPlatformError(error) && error.code === 'unauthorized') {
      if (query.queryKey[0] !== 'me') options.onUnauthorized?.();
      return;
    }
    if (query.meta?.silent) return;
    // Only toast for queries that already had data (background refresh failures); first loads render inline.
    if (query.state.data !== undefined) notify.error(error, 'Refresh failed');
  };
  return qc;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: () => client.auth.me(),
  staleTime: 60_000,
  retry: false,
  meta: { silent: true },
});

/**
 * The episode record of an agent activity (`GET /tasks/{taskId}`), with the case it belongs to (`caseId`,
 * `activationId`; 20a). Internal: the workbench never shows it as a page of its own.
 */
export const taskQuery = (taskId: string) =>
  queryOptions({
    queryKey: keys.task(taskId),
    queryFn: ({ signal }) => client.call(api.tasks.get, { params: { taskId }, signal }),
  });

export const traceQuery = (taskId: string) =>
  queryOptions({
    queryKey: keys.trace(taskId),
    queryFn: ({ signal }) => client.tasks.trace({ params: { taskId }, signal }),
  });

/**
 * Durable model attribution of a task (call records + pinned bindings, first 100 calls). `null` when the server has
 * no such route (an older server answers 404), so the attribution falls back to the proposal's last model.
 */
export const modelCallsQuery = (taskId: string) =>
  queryOptions({
    queryKey: keys.modelCalls(taskId),
    queryFn: async ({ signal }) => {
      try {
        return await client.tasks.modelCalls({ params: { taskId }, query: { limit: 100 }, signal });
      } catch (error) {
        if (isPlatformError(error) && error.code === 'not_found') return null;
        throw error;
      }
    },
  });

export const ledgerQuery = (taskId: string) =>
  queryOptions({
    queryKey: keys.ledger(taskId),
    queryFn: ({ signal }) => client.ledger.actions({ query: { taskId }, signal }),
  });

export const auditQuery = (taskId?: string) =>
  queryOptions({
    queryKey: keys.audit(taskId),
    queryFn: ({ signal }) => client.audit.list({ query: taskId ? { taskId } : {}, signal }),
  });

export const approvalsQuery = (status?: ApprovalStatus, taskId?: string) =>
  queryOptions({
    queryKey: keys.approvals(status ?? 'all', taskId),
    queryFn: ({ signal }) =>
      client.approvals.list({
        query: { ...(status ? { status } : {}), ...(taskId ? { taskId } : {}) },
        signal,
      }),
    refetchInterval: 15_000,
  });

export const approvalQuery = (approvalId: string) =>
  queryOptions({
    queryKey: keys.approval(approvalId),
    queryFn: ({ signal }) => client.approvals.get({ params: { approvalId }, signal }),
  });

export const agentsQuery = queryOptions({ queryKey: keys.agents, queryFn: () => client.registry.agents() });
export const toolsQuery = queryOptions({ queryKey: keys.tools, queryFn: () => client.registry.tools() });
export const modelsQuery = queryOptions({ queryKey: keys.models, queryFn: () => client.registry.models() });

/**
 * Capabilities with input schemas. Falls back to `GET /registry/agents` (objective-only forms) when the
 * backend does not serve `GET /registry/capabilities` yet (route added by 02f; see port-changes.md).
 */
export const capabilitiesQuery = queryOptions({
  queryKey: keys.capabilities,
  queryFn: async (): Promise<{ items: CapabilityView[]; fallback: boolean }> => {
    try {
      const res = await client.registry.capabilities();
      return { items: res.items, fallback: false };
    } catch (error) {
      if (!(isPlatformError(error) && error.code === 'not_found')) throw error;
      const agents = await client.registry.agents();
      const items = agents.items.flatMap((a) =>
        a.definition.spec.capabilities.map<CapabilityView>((cap) => ({
          name: cap.name,
          version: cap.version,
          domain: a.definition.metadata.domain,
          title: cap.name,
          agent: a.definition.metadata.name,
          module: a.module,
          inputSchema: {},
          status: a.status,
          killSwitch: a.killSwitch,
        })),
      );
      return { items, fallback: true };
    }
  },
});

export const modulesQuery = queryOptions({ queryKey: keys.modules, queryFn: () => client.modules.list() });
export const sourcesQuery = queryOptions({
  queryKey: keys.sources,
  queryFn: () => client.knowledge.sources(),
});
export const healthQuery = queryOptions({
  queryKey: keys.health,
  queryFn: () => client.admin.health(),
  refetchInterval: 10_000,
  meta: { silent: true },
});
export const statsQuery = queryOptions({
  queryKey: keys.stats,
  queryFn: () => client.admin.stats(),
  refetchInterval: 5_000,
});
export const scenariosQuery = queryOptions({
  queryKey: keys.scenarios,
  queryFn: () => client.demo.scenarios.list(),
});

// model management (23) ----------------------------------------------------------------------

/** `GET /registry/model-management/status`: enabled state and what the caller may do (read-only states). */
export const mmStatusQuery = queryOptions({
  queryKey: keys.mmStatus,
  queryFn: ({ signal }) => client.modelManagement.status({ signal }),
});
export const mmConnectionsQuery = queryOptions({
  queryKey: keys.mmConnections,
  queryFn: ({ signal }) => client.modelManagement.connections({ signal }),
});
/** Catalogue pages (cursor pagination, 50 per page) for one filter set. */
export const mmCatalogQuery = (query: Omit<ModelCatalogQuery, 'limit' | 'cursor'>) =>
  infiniteQueryOptions({
    queryKey: keys.mmCatalog(query),
    queryFn: ({ signal, pageParam }) =>
      client.modelManagement.catalog({
        query: { ...query, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
export const mmProfilesQuery = queryOptions({
  queryKey: keys.mmProfiles,
  queryFn: ({ signal }) => client.modelManagement.profiles({ query: { limit: 100 }, signal }),
});
export const mmProfileQuery = (name: string) =>
  queryOptions({
    queryKey: keys.mmProfile(name),
    queryFn: ({ signal }) => client.modelManagement.profile({ params: { name }, signal }),
  });
export const mmAuditQuery = (target?: string) =>
  queryOptions({
    queryKey: keys.mmAudit(target),
    queryFn: ({ signal }) =>
      client.modelManagement.audit({ query: { limit: 20, ...(target ? { target } : {}) }, signal }),
    meta: { silent: true },
  });
